/**
 * Tabs (U5): segmented tablist with a sliding glass indicator and roving
 * arrow-key navigation (automatic activation).
 */
import {
  useLayoutEffect,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cx } from './cx';

export interface TabItem {
  id: string;
  label: ReactNode;
  /** Small count/pill after the label. */
  badge?: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: ReadonlyArray<TabItem>;
  value: string;
  onChange: (id: string) => void;
  size?: 'sm' | 'md';
  'aria-label': string;
  className?: string;
}

export function Tabs({
  items,
  value,
  onChange,
  size = 'md',
  'aria-label': ariaLabel,
  className,
}: TabsProps): JSX.Element {
  const listRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef(new Map<string, HTMLButtonElement>());
  const [indicator, setIndicator] = useState<{ x: number; w: number } | null>(null);

  useLayoutEffect(() => {
    function measure(): void {
      const el = tabRefs.current.get(value);
      const list = listRef.current;
      if (el) {
        setIndicator({ x: el.offsetLeft, w: el.offsetWidth });
        if (list) {
          const left = el.offsetLeft;
          const right = left + el.offsetWidth;
          if (left < list.scrollLeft) list.scrollTo({ left: Math.max(0, left - 8) });
          else if (right > list.scrollLeft + list.clientWidth) {
            list.scrollTo({ left: right - list.clientWidth + 8 });
          }
        }
      } else setIndicator(null);
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [value, items]);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const enabled = items.filter((t) => !t.disabled);
    if (enabled.length === 0) return;
    const index = enabled.findIndex((t) => t.id === value);
    let next: TabItem | undefined;
    if (event.key === 'ArrowRight') next = enabled[(index + 1) % enabled.length];
    else if (event.key === 'ArrowLeft')
      next = enabled[(index - 1 + enabled.length) % enabled.length];
    else if (event.key === 'Home') next = enabled[0];
    else if (event.key === 'End') next = enabled[enabled.length - 1];
    if (next) {
      event.preventDefault();
      onChange(next.id);
      tabRefs.current.get(next.id)?.focus();
    }
  }

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cx('bl-tabs', size === 'sm' && 'bl-tabs--sm', className)}
      onKeyDown={handleKeyDown}
    >
      {indicator && (
        <span
          className="bl-tabs__indicator"
          style={{ transform: `translateX(${indicator.x}px)`, width: indicator.w }}
          aria-hidden="true"
        />
      )}
      {items.map((tab) => (
        <button
          key={tab.id}
          ref={(el) => {
            if (el) tabRefs.current.set(tab.id, el);
            else tabRefs.current.delete(tab.id);
          }}
          type="button"
          role="tab"
          className="bl-tabs__tab"
          aria-selected={tab.id === value}
          tabIndex={tab.id === value ? 0 : -1}
          disabled={tab.disabled}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
          {tab.badge != null && <span className="bl-tabs__badge">{tab.badge}</span>}
        </button>
      ))}
    </div>
  );
}
